import * as Sentry from "@sentry/react";
import { AppError, mapFirebaseError } from "./appError";

/**
 * Error codes that are the product working, not a fault, and therefore never
 * reach Sentry — the same treatment a blocked disposable-email sign-up gets.
 *
 * A fal content policy rejection is the safety system doing its job. The
 * backend already keeps it out of Sentry (`handler.ts`, `upscale.ts`,
 * `bria.ts`); without this list the browser would report the very same
 * rejection from every generation catch block that calls `reportError`, and
 * the exemption would only hold for half the pipeline. The user is still
 * told, by the notification the generation raises.
 */
const EXPECTED_ERROR_CODES = new Set(["generation/content_policy_violation"]);

/**
 * True when `error` is a known, expected outcome that must not be reported.
 * Accepts both an `AppError` and the raw callable rejection it is mapped from.
 */
export const isExpectedError = (error: unknown): boolean => {
  const mapped = mapFirebaseError(error);
  return mapped instanceof AppError && EXPECTED_ERROR_CODES.has(mapped.code);
};

/**
 * Reports an error to Sentry and logs it to the console.
 * Use this in catch blocks to ensure errors are tracked.
 *
 * @param error The error object or message
 * @param context Optional additional context to include in the Sentry report
 */
export const reportError = (error: unknown, context?: Record<string, any>) => {
  if (isExpectedError(error)) {
    console.warn(
      "[ErrorReport] expected outcome, not reported",
      error,
      context
    );
    return;
  }
  console.error("[ErrorReport]", error, context);

  Sentry.withScope((scope) => {
    if (context) {
      scope.setContext("additional_info", context);
    }
    Sentry.captureException(error);
  });
};

/**
 * Strips characters from a user-supplied file name before sending it to Sentry,
 * keeping only path-safe characters and capping length so personal info in
 * unusual file names isn't propagated verbatim into telemetry.
 */
export const sanitizeFileNameForReport = (name: string | undefined): string => {
  if (!name) return "";
  return name.replace(/[^a-zA-Z0-9._-]/g, "_").slice(0, 120);
};

/**
 * Logs a message to Sentry for non-exception events that still need tracking.
 */
export const logWarning = (message: string, context?: Record<string, any>) => {
  console.warn("[Warning]", message, context);

  Sentry.withScope((scope) => {
    if (context) {
      scope.setContext("additional_info", context);
    }
    Sentry.captureMessage(message, "warning");
  });
};
